import { currencySymbol } from "../../helper/commonHelper";
import React, { useContext, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Modal } from "react-bootstrap";
import Select from "react-select";
import DatePicker from "react-multi-date-picker";
import { useLoadingBar } from "react-top-loading-bar";
import { CSVLink } from "react-csv";
import toast from "react-hot-toast";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import { ACTION_ICONS, resolveRoleCode } from "../../config/siteConfig";
import ContentLoader from "../../components/loader/ContentLoader";
import { bookingsService } from "../../services/bookings.service";
import { SOMETHING_WENT_WRONG } from "../../constants/strings";
import DContext from "../../context/DContext";
import { formatDateToDisplay, formatDateYYYYMMDD } from "../../utils/dateUtils";
import { dropdownSelectStyles } from "../../utils/selectStyles";

const BOOKING_STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "confirmed", label: "Confirmed" },
  { value: "ticketed", label: "Ticketed" },
  { value: "cancelled", label: "Cancelled" },
  { value: "completed", label: "Completed" },
];

const PAYMENT_STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "paid", label: "Paid" },
  { value: "failed", label: "Failed" },
  { value: "refunded", label: "Refunded" },
];

const TRIP_TYPE_OPTIONS = [
  { value: "one_way", label: "One Way" },
  { value: "round_trip", label: "Round Trip" },
  { value: "multi_city", label: "Multi City" },
];

const ROUTE_TYPE_OPTIONS = [
  { value: "domestic", label: "Domestic" },
  { value: "international", label: "International" },
];

const selectStyles = {
  control: (base, state) => ({
    ...base,
    borderRadius: 8,
    border: "1px solid #e8edf5",
    background: "#f8f9fe",
    minHeight: 34,
    height: 34,
    fontSize: 13,
    boxShadow: "none",
    "&:hover": { borderColor: state.isFocused ? "var(--btn-bg)" : "#e8edf5" },
  }),
  valueContainer: (base) => ({ ...base, padding: "0 8px" }),
  indicatorsContainer: (base) => ({ ...base, height: 34 }),
  placeholder: (base) => ({ ...base, color: "#a0a8b8", fontSize: 13 }),
  menuPortal: (base) => ({ ...base, zIndex: 2000 }),
};

const initialFilters = {
  search: "",
  agent_code: "",
  buyer_supplier_code: "",
  supplier_code: "",
  booking_status: "",
  payment_status: "",
  trip_type: "",
  route_type: "",
  from_date: "",
  to_date: "",
};

const statusColors = (status) => {
  switch (status) {
    case "confirmed":
    case "ticketed":
    case "completed":
    case "paid":
      return { bg: "#d4edda", color: "#155724" };
    case "cancelled":
    case "failed":
      return { bg: "#f8d7da", color: "#721c24" };
    default:
      return { bg: "#fff3cd", color: "#856404" };
  }
};

const formatDate = (d) => (d ? String(d).slice(0, 10) : "-");
const label = (v) => (v ? String(v).replace(/_/g, " ") : "-");

const StatusPill = ({ value }) => {
  const c = statusColors(value);
  return (
    <span
      className="d-inline-block"
      style={{
        background: c.bg,
        color: c.color,
        borderRadius: 8,
        padding: "3px 10px",
        fontSize: 12,
        fontWeight: 600,
        textTransform: "capitalize",
      }}
    >
      {label(value)}
    </span>
  );
};

const Bookings = () => {
  const { start, complete } = useLoadingBar({ color: "blue", height: 2 });
  const navigate = useNavigate();

  const { authState} = useContext(DContext);

  const [loading, setLoading] = useState(false);
  const [bookingRes, setBookingRes] = useState({ pagination: {}, bookings: [] });

  const [pnrBooking, setPnrBooking] = useState(null); // booking whose PNR is being added
  const [pnrValue, setPnrValue] = useState("");
  const [savingPnr, setSavingPnr] = useState(false);

  const [pageValue, setPageValue] = useState(1);
  const [pagingLimit, setPagingLimit] = useState("10");
  const [goPage, setGoPage] = useState("");

  // a logged-in supplier (role_code 1003) only sees their own bookings
  const defaultFilters = {
    ...initialFilters,
    supplier_code: resolveRoleCode(authState) === 1003 ? authState?.userProfile?.supplier_code || "" : "",
  };

  const [filters, setFilters] = useState(defaultFilters);
  const [appliedFilters, setAppliedFilters] = useState(defaultFilters);

  // auth state may arrive after the first render
  useEffect(() => {
    if (defaultFilters.supplier_code) {
      setFilters((f) => ({ ...f, supplier_code: defaultFilters.supplier_code }));
      setAppliedFilters((f) => ({ ...f, supplier_code: defaultFilters.supplier_code }));
    }
  }, [defaultFilters.supplier_code]);

  useEffect(() => {
    loadBookings();
  }, [pagingLimit, pageValue, appliedFilters]);

  const showSwal = (title, text, icon) => {
    withReactContent(Swal).fire({ title, text, icon });
  };

  const loadBookings = async () => {
    try {
      setLoading(true);
      start();      
      const res = await bookingsService.list({
        page: Number(pageValue),
        limit: Number(pagingLimit),
        ...appliedFilters,
      });
      if (res?.success) {
        setBookingRes({
          pagination: res.resData?.pagination || {},
          bookings: res.resData?.data || [],
        });
      } else {
        toast.error(res?.message);
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error");
    } finally {
      setLoading(false);
      complete();
    }
  };

  const openPnrModal = (booking) => {
    setPnrBooking(booking);
    setPnrValue("");
  };

  const handleSavePnr = async (e) => {
    e.preventDefault();
    const pnr = pnrValue.trim().toUpperCase();
    if (!pnr) return toast.error("Please enter the PNR.");
    const confirm = await withReactContent(Swal).fire({
      title: "Update PNR?",
      text: `Set PNR ${pnr} for booking ${pnrBooking.booking_ref_id || "#" + pnrBooking.id}?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, update",
      cancelButtonText: "Cancel",
    });
    if (!confirm.isConfirmed) return;
    try {      
      setSavingPnr(true);
      const res = await bookingsService.updatePnr(pnrBooking.id, { pnr });
      if (res?.success) {
        toast.success(res.message || "PNR updated.");
        setBookingRes((cur) => ({
          ...cur,
          bookings: cur.bookings.map((b) => (b.id === pnrBooking.id ? { ...b, pnr } : b)),
        }));
        setPnrBooking(null);
      } else {
        toast.error(res?.message || "Could not update the PNR.");
      }
    } catch (err) {
      showSwal("Error", SOMETHING_WENT_WRONG, "error");
    } finally {
      setSavingPnr(false);
    }
  };

  const handleViewBooking = (bookingId) => {
    navigate("/booking-details/" + bookingId);
  };

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const handleSelectFilter = (name) => (o) => {
    setFilters({ ...filters, [name]: o ? o.value : "" });
  };

  const handleApply = (e) => {
    e?.preventDefault();
    if (filters.from_date && filters.to_date && filters.from_date > filters.to_date) {
      toast.error("From date cannot be after To date");
      return;
    }
    setPageValue(1);
    //setAppliedFilters({ ...filters });
    setAppliedFilters({
        ...filters,
        from_date: filters.from_date
          ? formatDateYYYYMMDD(filters.from_date)
          : "",
        to_date: filters.to_date
          ? formatDateYYYYMMDD(filters.to_date)
          : "",
      });
  };

  const handleReset = () => {
    setFilters(defaultFilters);
    setPageValue(1);
    setAppliedFilters(defaultFilters);
  };

  const handleGoPage = () => {
    const n = Number(goPage);
    if (n >= 1 && n <= totalPage) {
      setPageValue(n);
    }
    setGoPage("");
  };

  const handlePagingLimit = (e) => {
    setPageValue(1);
    setPagingLimit(e.target.value);
  };

  const bookings = bookingRes?.bookings || [];
  const totalRecords = bookingRes?.pagination?.total || 0;
  const totalPage = Math.max(1, Math.ceil(totalRecords / Number(pagingLimit)));
  const confirmedCount = bookings.filter((b) =>
    ["confirmed", "ticketed", "completed"].includes(b?.booking_status),
  ).length;
  const cancelledCount = bookings.filter((b) => b?.booking_status === "cancelled").length;
  const fromRecord = totalRecords === 0 ? 0 : (pageValue - 1) * Number(pagingLimit) + 1;
  const toRecord = Math.min(pageValue * Number(pagingLimit), totalRecords);

  const getPageNumbers = () => {
    if (totalPage <= 5) return Array.from({ length: totalPage }, (_, i) => i + 1);
    const sorted = [...new Set([1, 2, totalPage - 1, totalPage, pageValue - 1, pageValue, pageValue + 1])]
      .filter((n) => n >= 1 && n <= totalPage)
      .sort((a, b) => a - b);
    const out = [];
    sorted.forEach((n, i) => {
      if (i > 0 && n - sorted[i - 1] > 1) out.push("...");
      out.push(n);
    });
    return out;
  };

  const csvHeaders = [
    { label: "Booking Ref", key: "booking_ref_id" },
    { label: "PNR", key: "pnr" },
    { label: "Supplier", key: "supplier_name" },
    { label: "Agent Code", key: "agent_code" },
    { label: "Origin", key: "origin" },
    { label: "Destination", key: "destination" },
    { label: "Departure", key: "departure_date" },
    { label: "Trip Type", key: "trip_type" },
    { label: "Route Type", key: "route_type" },
    { label: "Passengers", key: "pax_count" },
    { label: "Total Fare", key: "total_fare" },
    { label: "Currency", key: "currency_code" },
    { label: "Payment Status", key: "payment_status" },
    { label: "Booking Status", key: "booking_status" },
  ];

  const csvData = bookings.map((b) => ({
    ...b,
    supplier_name: b?.supplier?.agency_name || "",
    pax_count: (b?.adult_count || 0) + (b?.child_count || 0) + (b?.infant_count || 0),
  }));

  const pageBtn = (active, disabled) => ({
    minWidth: 32,
    height: 32,
    padding: "0 8px",
    borderRadius: 8,
    border: active ? "1px solid var(--page-active-bg)" : "1px solid #e8edf5",
    background: active ? "var(--page-active-bg)" : "#fff",
    color: active ? "var(--page-active-text)" : disabled ? "#c3cad8" : "#444",
    fontSize: 13,
    fontWeight: 600,
    cursor: disabled ? "default" : "pointer",
  });

  const chip = (color, bg, border) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 14px",
    borderRadius: 10,
    background: bg,
    border: `1px solid ${border}`,
    color,
    fontSize: 13,
  });

  const filterInput = {
    borderRadius: 8,
    border: "1px solid #e8edf5",
    background: "#f8f9fe",
    fontSize: 13,
    height: 34,
  };

  const headerBtn = {
    borderRadius: 8,
    height: 34,
    fontSize: 13,
    fontWeight: 600,
  };

  const selectFilter = (name, options, placeholder, cls = "col-6 col-md-2") => (
    <div className={cls}>
      <Select
        options={options}
        value={options.find((o) => o.value === filters[name]) || null}
        onChange={handleSelectFilter(name)}
        placeholder={placeholder}
        isClearable
        menuPortalTarget={document.body}
        styles={dropdownSelectStyles}
      />
    </div>
  );

  const paxTotal = (b) => (b?.adult_count || 0) + (b?.child_count || 0) + (b?.infant_count || 0);

  return (
    <>
      <section className="section dashboard">
        <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
          <div>
            <h4 className="m-0 fw-bold" style={{ color: "var(--page-heading)", fontSize: 20 }}>
              Bookings
            </h4>
            <nav className="d-flex align-items-center gap-1 mt-1" style={{ fontSize: 12, color: "#475569" }}>
              <Link to="/dashboard" style={{ color: "#475569" }}>
                <i className="bi bi-house-door"></i>
              </Link>
              <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
              <span>Bookings</span>
              <i className="bi bi-chevron-right" style={{ fontSize: 9 }}></i>
              <span className="fw-semibold" style={{ color: "#4a5568" }}>
                Booking List
              </span>
            </nav>
          </div>
          <div className="d-flex align-items-center gap-2">
            <CSVLink
              data={csvData}
              headers={csvHeaders}
              filename="bookings.csv"
              className="btn btn-sm d-flex align-items-center gap-1 px-3"
              style={{ ...headerBtn, background: "#fff", border: "1px solid #e8edf5", color: "#444" }}
            >
              <i className="bi bi-download"></i> Export
            </CSVLink>
          </div>
        </div>

        <form onSubmit={handleApply} className="card border-0 shadow-sm mb-3" style={{ borderRadius: 14 }}>
          <div className="card-body p-3">
            <div className="row g-2 align-items-center">
              <div className="col-12 col-md">
                <input
                  type="text"
                  className="form-control"
                  name="search"
                  placeholder="Search booking ref, PNR, passenger"
                  value={filters.search}
                  onChange={handleFilterChange}
                  style={filterInput}
                />
              </div>
              <div className="col-6 col-md-2">
                <input
                  type="text"
                  className="form-control"
                  name="agent_code"
                  placeholder="Agent Code"
                  value={filters.agent_code}
                  onChange={handleFilterChange}
                  style={filterInput}
                />
              </div>
              <div className="col-6 col-md-2">
                <input
                  type="text"
                  className="form-control"
                  name="buyer_supplier_code"
                  placeholder="Buyer Supplier Code"
                  value={filters.buyer_supplier_code}
                  onChange={handleFilterChange}
                  style={filterInput}
                />
              </div>
              {selectFilter("booking_status", BOOKING_STATUS_OPTIONS, "Select Booking Status")}
              {selectFilter("payment_status", PAYMENT_STATUS_OPTIONS, "Select Payment Status")}
            </div>
            <div className="row g-2 align-items-center mt-0">
              {selectFilter("trip_type", TRIP_TYPE_OPTIONS, "Select Trip Type", "col-6 col-md-2")}
              {selectFilter("route_type", ROUTE_TYPE_OPTIONS, "Select Route Type", "col-6 col-md-2")}
              <div className="col-6 col-md-2">
                <DatePicker
                  onOpenPickNewDate={false}
                  value={filters.from_date || null}
                  onChange={(d) => setFilters({ ...filters, from_date: d ? d.format("DD-MM-YYYY") : "" })}
                  format="DD-MM-YYYY"
                  placeholder="From date"
                  editable={false}
                  inputClass="form-control"
                  containerStyle={{ width: "100%" }}
                  style={{ ...filterInput, width: "100%" }}
                />
              </div>
              <div className="col-6 col-md-2">
                <DatePicker
                  onOpenPickNewDate={false}
                  value={filters.to_date || null}
                  onChange={(d) => setFilters({ ...filters, to_date: d ? d.format("DD-MM-YYYY") : "" })}
                  format="DD-MM-YYYY"
                  placeholder="To date"
                  editable={false}
                  inputClass="form-control"
                  containerStyle={{ width: "100%" }}
                  style={{ ...filterInput, width: "100%" }}
                />
              </div>
              <div className="col-12 col-md-auto d-flex gap-2">
                <button
                  type="submit"
                  className="btn btn-sm d-flex align-items-center gap-1 text-white px-3"
                  style={{ ...headerBtn, background: "var(--btn-bg)" }}
                >
                  <i className="bi bi-search"></i> Apply
                </button>
                <button
                  type="button"
                  onClick={handleReset}
                  className="btn btn-sm d-flex align-items-center gap-1 px-3"
                  style={{ ...headerBtn, fontWeight: 400, background: "#fff", border: "1px solid #e8edf5", color: "#444" }}
                >
                  <i className="bi bi-arrow-counterclockwise"></i> Reset
                </button>
              </div>
            </div>
          </div>
        </form>

        <div className="d-flex flex-wrap gap-2 mb-3">
          <span style={chip("#e8590c", "#fff4e6", "#ffd8a8")}>
            <i className="bi bi-circle-fill" style={{ fontSize: 7 }}></i>
            <b>{totalRecords}</b> Total Bookings
          </span>
          <span style={chip("#0f8a5f", "#e6f7f1", "#b7ebd6")}>
            <i className="bi bi-circle-fill" style={{ fontSize: 7 }}></i>
            <b>{confirmedCount}</b> Confirmed on this page
          </span>
          <span style={chip("#1971c2", "#e7f5ff", "#a5d8ff")}>
            <i className="bi bi-circle-fill" style={{ fontSize: 7 }}></i>
            <b>{cancelledCount}</b> Cancelled on this page
          </span>
        </div>

        <div className="card border-0 shadow-sm" style={{ borderRadius: 14, overflow: "hidden" }}>
          <div style={{ position: "relative" }}>
            {loading && (
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: "rgba(255,255,255,0.6)",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  zIndex: 10,
                }}
              >
                <ContentLoader loaderType="circleSpin" />
              </div>
            )}
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead>
                  <tr style={{ background: "#f8f9fe" }}>
                    {["S.No.", "Booking Date", "PNR", "Airline", "Sector", "Departure Date", "Buyer Name", "Seller Name", "Seats", "Sold From", "Status", "Amount", "Action"].map(
                      (h, i) => (
                        <th
                          key={i}
                          style={{
                            fontSize: "11px",
                            fontWeight: "700",
                            color: "#475569",
                            textTransform: "uppercase",
                            letterSpacing: "0.6px",
                            borderBottom: "2px solid #e8edf5",
                            padding: "10px 12px",
                            whiteSpace: "nowrap",
                            background: "#f8f9fe",
                          }}
                        >
                          {h}
                        </th>
                      ),
                    )}
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((booking, sno_i) => (
                    <tr key={booking?.id} style={{ borderBottom: "1px solid #f0f2f5" }}>
<td className="ps-3" style={{ fontSize: 13 }}>{(pageValue - 1) * Number(pagingLimit) + sno_i + 1}</td>
                      <td>
                        <div className="fw-semibold" style={{ fontSize: 13 }}>
                          {booking?.created_at ? formatDateToDisplay(formatDate(booking.created_at)) : "-"}
                        </div>
                        <div className="text-muted" style={{ fontSize: 11 }}>
                          {booking?.created_at ? new Date(booking.created_at).toLocaleTimeString("en-GB") : ""}
                        </div>
                      </td>
                      <td style={{ fontSize: 13 }}>
                       {booking?.pnr ? (
                        <span className="d-inline-block" style={{ background: "#e7e3e3", color: "#000", borderRadius: 8, padding: "2px 10px", fontSize: 11, fontWeight: 600 }}>{booking.pnr}</span>
                       ) : (
                        <button type="button" className="bk-pnr-btn" onClick={() => openPnrModal(booking)}>
                          <i className="bi bi-plus-circle"></i> Update
                        </button>
                       )}
                      </td>
                      <td style={{ fontSize: 13, color:'#0b4f72', fontWeight:700 }}>
                        {booking?.full_fare_desc?.airline?.airline_code}-{booking?.full_fare_desc?.flight_number || "-"}
                      </td>
                      <td className="fw-semibold" style={{ fontSize: 13 }}>
                        {booking?.origin} - {booking?.destination}
                      </td>
                      <td>
                        <div className="fw-semibold" style={{ fontSize: 13 }}>
                          {formatDateToDisplay(formatDate(booking?.departure_date))}
                        </div>
                        <div className="text-muted" style={{ fontSize: 11 }}>{formatDate(booking?.departure_date)}</div>
                      </td>
                      <td style={{ fontSize: 13 }}>                        
                        <div className="fw-semibold" style={{ fontSize: 13 }}>
                          {booking?.agent?.company_name || booking?.buyerSupplier?.agency_name || "-"}
                        </div>
                        <div className="text-muted" style={{ fontSize: 10 }}>
                          {booking?.agent?.agent_code || booking?.buyerSupplier?.supplier_code || "-"}
                        </div>
                      </td>
                      <td style={{ fontSize: 13 }}>
                      <div className="fw-semibold" style={{ fontSize: 13 }}>
                          {booking?.supplier?.agency_name || "-"}
                        </div>
                        <div className="text-muted" style={{ fontSize: 10 }}>
                         <span style={{ color:'#0b4f72', fontWeight:700 }}>{booking?.supplier?.custom_code || "-"}</span> - {booking?.supplier?.supplier_code || "-"}
                        </div>
                      </td>
                      <td style={{ fontSize: 13 }}>                     
                      <span
  className="d-inline-block"
  style={{
    background: "#e7e3e3",
    color: "#000",
    borderRadius: "8px",
    padding: "2px 10px",
    fontSize: "12px",
    fontWeight: 600,
  }}
>
  {booking?.passengers?.length ?? 0}
</span>
                      </td>
                      <td className="text-uppercase" style={{ fontSize: 13 }}> 
                      <span
  className="d-inline-block"
  style={{
    background: "#e7e3e3",
    color: "#000",
    borderRadius: "8px",
    padding: "2px 10px",
    fontSize: "12px",    
    textTransform: "capitalize",
  }}
>
  {booking?.call_type}
</span>
                      </td>
                      <td>
                        <StatusPill value={booking?.booking_status} />
                      </td>
                      <td className="fw-semibold" style={{ fontSize: 13 }}>
                        {currencySymbol(booking?.currency_code) + " " + (booking?.total_fare ?? "0.00")}
                      </td>
                      <td className="text-center">
                        <div className="d-flex align-items-center justify-content-center gap-2">
                          <button
                            className="btn btn-sm d-flex align-items-center justify-content-center"
                            onClick={() => handleViewBooking(booking?.id)}
                            title="View"
                            style={{
                              width: 32,
                              height: 32,
                              borderRadius: 8,
                              background: "#e6f7f1",
                              color: "#0f8a5f",
                              border: "none",
                              padding: 0,
                            }}
                          >
                            <i className={`bi ${ACTION_ICONS.view}`} data-action="view" style={{ fontSize: 13 }}></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!loading && bookings.length === 0 && (
                    <tr>
                      <td colSpan={13} className="text-center text-muted py-4" style={{ fontSize: 13 }}>
                        No bookings found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
          <div
            className="d-flex align-items-center justify-content-between flex-wrap gap-2 px-3 py-3"
            style={{ borderTop: "1px solid #eef1f6", fontSize: 13, color: "#6b7a90" }}
          >
            <div>
              Showing{" "}
              <b>
                {fromRecord}–{toRecord}
              </b>{" "}
              of <b>{totalRecords}</b> bookings
            </div>
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <span>Rows</span>
              <select
                className="form-select form-select-sm"
                style={{ width: 100, borderRadius: 8 }}
                value={pagingLimit}
                onChange={handlePagingLimit}
              >
                {["10", "20", "50", "100", "500"].map((v) => (
                  <option key={v} value={v}>
                    {v}/page
                  </option>
                ))}
              </select>
              <span>Go</span>
              <input
                type="number"
                min="1"
                max={totalPage}
                className="form-control form-control-sm text-center"
                style={{ width: 60, borderRadius: 8 }}
                value={goPage}
                onChange={(e) => setGoPage(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleGoPage()}
                placeholder={String(pageValue)}
              />
              <span>/ {totalPage}</span>
              <button type="button" style={pageBtn(false, false)} onClick={handleGoPage}>
                Go
              </button>
              <div className="d-flex align-items-center gap-1">
                <button
                  type="button"
                  disabled={pageValue <= 1}
                  style={pageBtn(false, pageValue <= 1)}
                  onClick={() => setPageValue(1)}
                >
                  <i className="bi bi-chevron-double-left"></i>
                </button>
                <button
                  type="button"
                  disabled={pageValue <= 1}
                  style={pageBtn(false, pageValue <= 1)}
                  onClick={() => setPageValue(pageValue - 1)}
                >
                  <i className="bi bi-chevron-left"></i>
                </button>
                {getPageNumbers().map((n, i) =>
                  n === "..." ? (
                    <span key={"e" + i} className="px-1">
                      ...
                    </span>
                  ) : (
                    <button type="button" key={n} style={pageBtn(n === pageValue, false)} onClick={() => setPageValue(n)}>
                      {n}
                    </button>
                  ),
                )}
                <button
                  type="button"
                  disabled={pageValue >= totalPage}
                  style={pageBtn(false, pageValue >= totalPage)}
                  onClick={() => setPageValue(pageValue + 1)}
                >
                  <i className="bi bi-chevron-right"></i>
                </button>
                <button
                  type="button"
                  disabled={pageValue >= totalPage}
                  style={pageBtn(false, pageValue >= totalPage)}
                  onClick={() => setPageValue(totalPage)}
                >
                  <i className="bi bi-chevron-double-right"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        .bk-pnr-btn { display:inline-flex; align-items:center; gap:5px; padding:2px 10px; font-size:11px; font-weight:700; border:1px solid var(--btn-bg) !important; border-radius:8px; background:#fff !important; color:var(--btn-bg) !important; box-shadow:none !important; }
        .bk-pnr-btn:hover { background:var(--btn-bg) !important; color:#fff !important; }
      `}</style>

      {/* ── Update PNR modal ── */}
      <Modal show={!!pnrBooking} onHide={() => !savingPnr && setPnrBooking(null)} centered size="lg" backdrop="static">
        {pnrBooking && (() => {
          const fd = pnrBooking.full_fare_desc;
          const city = (loc, fallback) => `${loc?.city_name || "-"} (${loc?.airport_code || fallback || "-"})`;
          const time = (loc) => String(loc?.time || "-").slice(0, 5);
          const details = [
            { label: "Booking Ref", value: pnrBooking.booking_ref_id || "-" },
            { label: "Supplier", value: pnrBooking.supplier?.agency_name || "-" },
            { label: "Passengers", value: pnrBooking.passengers?.length ?? 0 },
            { label: "Departure", value: city(fd?.origin, pnrBooking.origin) },
            { label: "Arrival", value: city(fd?.destination, pnrBooking.destination) },
            { label: "Dept. Date", value: formatDate(pnrBooking.departure_date) },
            { label: "Dept – Arr Time", value: `${time(fd?.origin)} – ${time(fd?.destination)}` },
            { label: "Airline | Flight No", value: `${fd?.airline?.airline_name || fd?.airline?.airline_code || "-"} | ${fd?.airline?.airline_code || "-"} | ${fd?.flight_number || "-"}` },
          ];
          return (
            <form onSubmit={handleSavePnr}>
              <Modal.Header closeButton>
                <div className="d-flex align-items-center gap-3">
                  <div style={{ width: 38, height: 38, borderRadius: 10, background: "rgba(255,255,255,.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <i className="bi bi-upc-scan text-white" style={{ fontSize: 15 }}></i>
                  </div>
                  <div>
                    <h6 className="mb-0 fw-bold" style={{ color: "#fff", fontSize: 15 }}>Update PNR</h6>
                    <p className="mb-0" style={{ fontSize: 11, color: "#fff" }}>Add the airline PNR for this booking</p>
                  </div>
                </div>
              </Modal.Header>
              <Modal.Body className="px-4 pb-3">
                <div className="row g-3 mb-3">
                  {details.map((f) => (
                    <div className="col-6 col-md-4" key={f.label}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: "#6c757d", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 2 }}>{f.label}</div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "#1f2a44" }}>{f.value}</div>
                    </div>
                  ))}
                </div>
                <label htmlFor="booking-pnr" className="form-label mb-1" style={{ fontSize: 12, fontWeight: 600, color: "#475569" }}>
                  PNR <span className="text-danger">*</span>
                </label>
                <input
                  id="booking-pnr"
                  className="form-control"
                  value={pnrValue}
                  onChange={(e) => setPnrValue(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))}
                  placeholder="Enter PNR"
                  maxLength={20}
                  autoFocus
                  required
                />
              </Modal.Body>
              <Modal.Footer>
                <button type="button" className="btn btn-sm px-3" disabled={savingPnr} onClick={() => setPnrBooking(null)} style={{ border: "1px solid #dbe2ea", borderRadius: 8, background: "#fff", color: "#475569" }}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-sm px-4 text-white" disabled={savingPnr} style={{ borderRadius: 8, background: "var(--btn-bg)", fontWeight: 600 }}>
                  {savingPnr ? "Saving..." : "Update PNR"}
                </button>
              </Modal.Footer>
            </form>
          );
        })()}
      </Modal>
    </>
  );
};

export default Bookings;
